import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from '../../../shared/components/Spinner';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { EmptyState } from '../../../shared/components/EmptyState';
import { formatUtcDateTime } from '../../../shared/utils/formatDate';
import { useAuth } from '../../../contexts/AuthContext';
import type { FeatureFlag, FeatureFlagAuditEntry } from '../../../models/featureFlag';
import { fetchFeatureFlagAuditHistory } from '../../../services/admin/featureFlagService';

export interface FeatureFlagAuditDrawerProps {
  visible: boolean;
  flag: FeatureFlag | null;
  onClose: () => void;
}

function formatMetadata(metadata: string | null): string {
  if (!metadata) {
    return '—';
  }
  try {
    return JSON.stringify(JSON.parse(metadata), null, 2);
  } catch {
    return metadata;
  }
}

/** Read-only audit history for one feature flag. Reuses the shared modal shell (see ConfirmModal). */
export function FeatureFlagAuditDrawer({ visible, flag, onClose }: FeatureFlagAuditDrawerProps): React.ReactElement | null {
  const { getAccessToken } = useAuth();
  const panelRef = useRef<HTMLDivElement>(null);

  const [entries, setEntries] = useState<FeatureFlagAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible || !flag) {
      return;
    }
    setError(null);
    setEntries([]);
    setLoading(true);
    fetchFeatureFlagAuditHistory(flag.id, getAccessToken)
      .then((result) => setEntries(result))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load audit history.'))
      .finally(() => setLoading(false));
  }, [visible, flag, getAccessToken]);

  useEffect(() => {
    if (!visible || !flag) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, flag, onClose]);

  if (!visible || !flag) {
    return null;
  }

  return (
    <div className="bo-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        className="bo-modal-panel bo-audit-drawer-panel"
        role="dialog"
        aria-modal
        aria-labelledby="bo-feature-flag-audit-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bo-feature-flag-audit-title" className="bo-modal-title">
          Audit history · {flag.key}
        </h2>

        {loading ? <Spinner label="Loading audit history…" /> : null}
        {!loading && error ? <ErrorAlert message={error} /> : null}

        {!loading && !error && entries.length === 0 ? (
          <EmptyState title="No audit history" description="No recorded changes for this feature flag yet." />
        ) : null}

        {!loading && !error && entries.length > 0 ? (
          <ul className="bo-audit-drawer-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {entries.map((entry) => (
              <li key={entry.id} style={{ borderBottom: '1px solid #e2e8f0', padding: '0.65rem 0' }}>
                <div className="bo-detail-fields-grid">
                  <div className="bo-detail-field">
                    <span className="bo-detail-field-label">Outcome</span>
                    <span className="bo-detail-field-value">
                      <span className="bo-status-pill bo-status-active">{entry.outcome}</span>
                    </span>
                  </div>
                  <div className="bo-detail-field">
                    <span className="bo-detail-field-label">Actor</span>
                    <span className="bo-detail-field-value">{entry.email || '—'}</span>
                  </div>
                  <div className="bo-detail-field">
                    <span className="bo-detail-field-label">Timestamp (UTC)</span>
                    <span className="bo-detail-field-value">{formatUtcDateTime(entry.createdAt)}</span>
                  </div>
                  <div className="bo-detail-field">
                    <span className="bo-detail-field-label">Reason</span>
                    <span className="bo-detail-field-value">{entry.reason || '—'}</span>
                  </div>
                </div>
                <details>
                  <summary className="bo-muted">Details</summary>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {formatMetadata(entry.metadata)}
                  </pre>
                </details>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="bo-actions" style={{ marginTop: '1.25rem', justifyContent: 'flex-end' }}>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
